/**
 * Hook for Google Analytics integration
 *
 * Manages Google Analytics tracking for forms.
 * Automatically loads config from form submission context and tracks events.
 */

import { useEffect, useState } from 'react';
import { useGoogleAnalytics } from './use_google_analytics';
import { useFormSubmission } from '../contexts/form-submission-context';
import type { GoogleAnalyticsConfig } from '../services/google-analytics.service';

export function useGoogleAnalyticsIntegration() {
  const { form, getIntegrationConfig } = useFormSubmission();
  const [gaConfig, setGaConfig] = useState<GoogleAnalyticsConfig | null>(null);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const [hasTrackedStart, setHasTrackedStart] = useState(false);

  // Initialize Google Analytics
  const {
    trackFormView,
    trackFormStart,
    trackFormSubmission,
    isConfigured
  } = useGoogleAnalytics(gaConfig);

  // Load Google Analytics config from form context
  useEffect(() => {
    if (!form?.id) {
      console.log('[GA Integration] No form ID yet');
      return;
    }

    console.log('[GA Integration] Loading config for form:', form.id);
    const config = getIntegrationConfig('google-analytics');
    console.log('[GA Integration] Config loaded:', config);
    setGaConfig(config);
  }, [form?.id, getIntegrationConfig]);

  // Track form view
  useEffect(() => {
    console.log('[GA Integration] View tracking check:', {
      formId: form?.id,
      formTitle: form?.title,
      isConfigured,
      hasTrackedView
    });

    if (form?.id && form?.title && isConfigured && !hasTrackedView) {
      console.log('[GA Integration] Tracking form view:', form.id, form.title);
      trackFormView(form.id, form.title);
      setHasTrackedView(true);
    }
  }, [form?.id, form?.title, isConfigured, hasTrackedView, trackFormView]);

  // Track form start (when user interacts with first field)
  useEffect(() => {
    if (!form?.id || !form?.title || !isConfigured || hasTrackedStart) {
      console.log('[GA Integration] Form start tracking not ready:', {
        formId: form?.id,
        formTitle: form?.title,
        isConfigured,
        hasTrackedStart
      });
      return;
    }

    const handleFirstInteraction = () => {
      if (!hasTrackedStart) {
        console.log('[GA Integration] Tracking form start:', form.id, form.title);
        trackFormStart(form.id, form.title);
        setHasTrackedStart(true);
      }
    };

    console.log('[GA Integration] Setting up form start listener');
    // Listen for first focus on any form field
    const formElement = document.querySelector('form');
    formElement?.addEventListener('focusin', handleFirstInteraction, { once: true });

    return () => {
      formElement?.removeEventListener('focusin', handleFirstInteraction);
    };
  }, [form?.id, form?.title, isConfigured, hasTrackedStart, trackFormStart]);

  return {
    isConfigured,
    trackFormSubmission: (submissionId?: number) => {
      if (form?.id && isConfigured) {
        console.log('[GA Integration] Tracking form submission:', form.id, submissionId);
        trackFormSubmission(form.id, submissionId, form.title);
      } else {
        console.log('[GA Integration] Cannot track submission:', {
          formId: form?.id,
          isConfigured
        });
      }
    },
  };
}

